import { useCallback, useEffect, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useAppSelector } from "@/shared/hooks";
import type { ContractFormValues } from "@/features/hr/contracts/types";

export interface UseLeaveProtectionReturn {
  modalOpen: boolean;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  requestConfirmation: (action: () => void) => void;
}

export function useLeaveProtection(methods: UseFormReturn<ContractFormValues>): UseLeaveProtectionReturn {
  const { formState } = methods;
  const isReadyForSigning = useAppSelector((state) => state.workContract.isReadyForSigning);

  const [modalOpen, setModalOpen] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const requestConfirmation = useCallback(
    (action: () => void) => {
      if (isReadyForSigning) {
        action();
        return;
      }
      pendingActionRef.current = action;
      setModalOpen(true);
    },
    [isReadyForSigning],
  );

  function confirmNavigation() {
    if (pendingActionRef.current) {
      pendingActionRef.current();
      pendingActionRef.current = null;
    }
    setModalOpen(false);
  }

  function cancelNavigation() {
    pendingActionRef.current = null;
    setModalOpen(false);
  }

  useEffect(() => {
    function handler(e: BeforeUnloadEvent) {
      if (!formState.isDirty || formState.isValid || isReadyForSigning) return;
      e.preventDefault();
      e.returnValue = "";
    }

    window.addEventListener("beforeunload", handler);

    return () => {
      window.removeEventListener("beforeunload", handler);
    };
  }, [formState.isDirty, formState.isValid, isReadyForSigning]);

  useEffect(() => {
    const originalPush = window.history.pushState;
    const originalReplace = window.history.replaceState;
    window.history.pushState = (...args: Parameters<typeof window.history.pushState>) => {
      if (formState.isDirty && !formState.isValid && !isReadyForSigning) {
        requestConfirmation(() => {
          originalPush.apply(window.history, args);
        });
        return;
      }
      originalPush.apply(window.history, args);
    };
    window.history.replaceState = (...args: Parameters<typeof window.history.replaceState>) => {
      if (formState.isDirty && !formState.isValid && !isReadyForSigning) {
        requestConfirmation(() => {
          originalReplace.apply(window.history, args);
        });
        return;
      }
      originalReplace.apply(window.history, args);
    };

    return () => {
      window.history.pushState = originalPush;
      window.history.replaceState = originalReplace;
    };
  }, [formState.isDirty, formState.isValid, isReadyForSigning, requestConfirmation]);

  return {
    modalOpen,
    confirmNavigation,
    cancelNavigation,
    requestConfirmation,
  };
}

